import { useState } from "react";
import { CheckCircle2, Handshake, PackageCheck, Percent } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEO from "../components/SEO";
import { Reveal, SectionTag } from "../components/bits";

const PERKS = [
  { icon: Percent, title: "Trade pricing", text: "Volume discounts across the full range — the more fixtures, the sharper the price." },
  { icon: PackageCheck, title: "Priority stock", text: "Reserved allocation on new batches so your project never waits on a restock." },
  { icon: Handshake, title: "Dedicated support", text: "A direct line to the team for room planning, spectrum advice and after-sales care." },
];

const EMPTY = { name: "", business: "", abn: "", email: "", phone: "", message: "" };

const FIELD = "w-full rounded-xl border border-[#39FF14]/20 bg-[#0A150F] px-4 py-3 text-sm text-[#F0FDF4] placeholder-[#9FB8A8]/50 outline-none transition-colors focus:border-[#39FF14]/60";

export default function Wholesale() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [doneId, setDoneId] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await api.post("/wholesale", form);
      setDoneId(r.data.enquiry_id);
    } catch {
      toast.error("Couldn't send your enquiry — please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 pb-28 pt-28 sm:px-8" data-testid="wholesale-page">
      <SEO
        title="Wholesale & Trade — LED Grow Lights at Trade Pricing | SuperTechLED"
        description="SuperTechLED wholesale and trade accounts for grow shops, commercial facilities and installers: volume pricing, priority stock and dedicated support across Australia."
      />
      <Reveal>
        <SectionTag>Wholesale &amp; trade</SectionTag>
        <h1 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl">
          Kit out your shop or facility
        </h1>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#9FB8A8] sm:text-base">
          Grow shops, commercial cultivators and installers — get trade pricing on the full SuperTechLED range,
          from compact COBs to the 1000W StarShip. Tell us about your setup and we'll come back with a quote
          within one business day.
        </p>
      </Reveal>

      <div className="mt-12 grid gap-6 sm:grid-cols-3">
        {PERKS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <div className="h-full rounded-3xl border border-[#39FF14]/15 bg-[#071009] p-7">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#39FF14]/25 bg-[#0A150F]">
                <p.icon className="h-5 w-5 text-[#39FF14]" />
              </span>
              <h2 className="font-display mt-5 text-lg font-bold text-[#F0FDF4]">{p.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-[#9FB8A8]">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-14">
        <div className="mx-auto max-w-2xl rounded-3xl border border-[#39FF14]/15 bg-[#071009] p-7 sm:p-10">
          {doneId ? (
            <div className="text-center" data-testid="wholesale-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-[#39FF14]" />
              <h2 className="font-display mt-5 text-2xl font-bold text-[#F0FDF4]">Enquiry received</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#9FB8A8]">
                Thanks — reference <span className="font-mono2 text-[#39FF14]">{doneId}</span>. We'll review your
                setup and reply with trade pricing within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="wholesale-form">
              <h2 className="font-display text-xl font-bold text-[#F0FDF4] sm:text-2xl">Request a trade account</h2>
              <p className="mt-2 text-sm text-[#9FB8A8]">A few details and we'll get a quote back to you.</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <input required data-testid="wholesale-name" className={FIELD} placeholder="Your name *" value={form.name} onChange={set("name")} />
                <input required data-testid="wholesale-business" className={FIELD} placeholder="Business name *" value={form.business} onChange={set("business")} />
                <input data-testid="wholesale-abn" className={FIELD} placeholder="ABN" value={form.abn} onChange={set("abn")} />
                <input data-testid="wholesale-phone" className={FIELD} placeholder="Phone" value={form.phone} onChange={set("phone")} />
                <input required type="email" data-testid="wholesale-email" className={`${FIELD} sm:col-span-2`} placeholder="Email *" value={form.email} onChange={set("email")} />
                <textarea required rows={4} data-testid="wholesale-message" className={`${FIELD} resize-none sm:col-span-2`} placeholder="What are you planning? Room sizes, quantities, timelines… *" value={form.message} onChange={set("message")} />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="wholesale-submit"
                className="mt-6 w-full rounded-full bg-[#39FF14] py-3.5 font-display text-sm font-bold text-[#050B07] transition-shadow hover:shadow-[0_0_28px_rgba(57,255,20,0.4)] disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          )}
        </div>
      </Reveal>
    </div>
  );
}
